import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { Search } from 'lucide-react'
import { useCurrentSite, useSiteList } from '@/app/sites'
import { cn } from '@/lib/utils'

const Ctx = createContext<{ open: () => void }>({ open: () => {} })
export const useCommand = () => useContext(Ctx)

type Cmd = { id: string; group: string; label: string; hint?: string; run: () => void }

export function CommandProvider({ children }: { children: ReactNode }) {
  const [isOpen, setOpen] = useState(false)
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen((o) => !o)
      }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [])
  return (
    <Ctx.Provider value={{ open: () => setOpen(true) }}>
      {children}
      {isOpen && <CommandModal onClose={() => setOpen(false)} />}
    </Ctx.Provider>
  )
}

function CommandModal({ onClose }: { onClose: () => void }) {
  const nav = useNavigate()
  const { siteId } = useCurrentSite()
  const { sites } = useSiteList()
  const [q, setQ] = useState('')
  const [sel, setSel] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 0)
    return () => clearTimeout(t)
  }, [])

  const all = useMemo<Cmd[]>(() => {
    const base = siteId ? `/site/${siteId}` : ''
    const go = (to: string) => () => nav(to)
    return [
      { id: 'home', group: 'Go to', label: 'Home', run: go('/home') },
      { id: 'portfolio', group: 'Go to', label: 'Portfolio', run: go('/portfolio') },
      { id: 'alerts', group: 'Go to', label: 'Alerts', run: go('/alerts') },
      { id: 'admin', group: 'Go to', label: 'Admin', run: go('/admin') },
      { id: 'account', group: 'Go to', label: 'Account', run: go('/account') },
      ...(siteId
        ? [
            { id: 'ov', group: 'Current site', label: 'Overview', run: go(base) },
            { id: 'as', group: 'Current site', label: 'Assessment', run: go(`${base}/assessment`) },
            { id: 'ev', group: 'Current site', label: 'Evidence', run: go(`${base}/evidence`) },
            { id: 'uw', group: 'Current site', label: 'Underwriting', run: go(`${base}/underwriting`) },
            { id: 'ob', group: 'Current site', label: 'Obligations', run: go(`${base}/obligations`) },
            { id: 'op', group: 'Current site', label: 'Operations', run: go(`${base}/operations`) },
          ]
        : []),
      ...sites.map((s) => ({ id: `site-${s.id}`, group: 'Sites', label: s.name, hint: s.pack, run: go(`/site/${s.id}`) })),
    ]
  }, [siteId, sites, nav])

  const filtered = useMemo(() => {
    const needle = q.toLowerCase().trim()
    if (!needle) return all
    return all.filter((c) => c.label.toLowerCase().includes(needle) || c.group.toLowerCase().includes(needle))
  }, [all, q])

  const run = (c?: Cmd) => {
    if (!c) return
    c.run()
    onClose()
  }
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSel((s) => Math.min(s + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSel((s) => Math.max(s - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      run(filtered[sel])
    } else if (e.key === 'Escape') {
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-black/50 p-4 pt-[12vh]"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        className="w-full max-w-xl overflow-hidden rounded-xl border bg-popover shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5 border-b px-4">
          <Search className="size-4 text-faint" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
              setSel(0)
            }}
            onKeyDown={onKeyDown}
            placeholder="Search sites and screens…"
            className="h-12 flex-1 bg-transparent text-[14px] outline-none placeholder:text-faint"
          />
          <kbd className="rounded border border-input px-1.5 py-0.5 font-mono text-[11px] text-faint">esc</kbd>
        </div>
        <div className="max-h-[52vh] overflow-y-auto p-1.5">
          {filtered.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">No matches</div>
          ) : (
            filtered.map((c, i) => {
              const header = i === 0 || filtered[i - 1].group !== c.group
              return (
                <div key={c.id}>
                  {header && (
                    <div className="px-3 pb-1 pt-2.5 font-display text-[10px] font-semibold uppercase tracking-wider text-faint">
                      {c.group}
                    </div>
                  )}
                  <button
                    onMouseEnter={() => setSel(i)}
                    onClick={() => run(c)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-[13.5px]',
                      i === sel ? 'bg-secondary text-foreground' : 'text-muted-foreground',
                    )}
                  >
                    <span className="flex-1">{c.label}</span>
                    {c.hint && <span className="text-[11px] text-faint">{c.hint}</span>}
                  </button>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
